import { lazy, Suspense, useEffect, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BatteryCharging, Building2, Loader2, MapPin, PlugZap, Users, Zap } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import BreadcrumbNav from "@/components/layout/BreadcrumbNav";
import Footer from "@/components/landing/Footer";
import ElavtalSearchCard from "@/components/common/ElavtalSearchCard";
import AppPromoBanner from "@/components/laddstationer/AppPromoBanner";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { seoHead } from "@/config/seoHead";
import { municipalityBySlug, placePreposition } from "@/data/municipalities";
import { formatNumber, localProfile, nearestMunicipalities } from "@/lib/localFacts";
import { getMunicipalityChargingStats } from "@/lib/chargingStations.functions";

const ChargingMap = lazy(() => import("@/components/laddstationer/ChargingMap"));

const radiusFor = (landArea: number) =>
  Math.round(Math.min(35, Math.max(5, Math.sqrt(landArea / Math.PI) * 0.8)));
const zoomFor = (radiusKm: number) => (radiusKm <= 6 ? 12 : radiusKm <= 12 ? 11 : radiusKm <= 22 ? 10 : 9);

export const Route = createFileRoute("/laddstationer/$slug")({
  beforeLoad: ({ params }) => {
    if (!municipalityBySlug(params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const m = municipalityBySlug(params.slug);
    const k = m?.displayName ?? "kommunen";
    const p = placePreposition(params.slug);
    return seoHead(`/laddstationer/${params.slug}`, {
      title: `Laddstationer ${p} ${k} » Hitta närmaste elbilsladdare | Elmarknad.se`,
      description: `Karta över laddstationer och snabbladdare ${p} ${k}. Se antal laddplatser, operatörer, uttagstyper och effekt – och hitta närmaste laddare för elbilen.`,
    });
  },
  component: KommunPage,
});

function KommunPage() {
  const { slug } = Route.useParams();
  const m = municipalityBySlug(slug)!;
  const k = m.displayName;
  const p = placePreposition(m.slug);
  const profile = localProfile(slug);
  const radiusKm = profile ? radiusFor(profile.landArea) : 10;
  const center = profile ? { lat: profile.lat, lng: profile.lon } : undefined;
  const nearby = nearestMunicipalities(slug, 8);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["laddstationer-kommun", slug],
    queryFn: () => getMunicipalityChargingStats({ data: { lat: center!.lat, lng: center!.lng, radiusKm } }),
    enabled: !!center,
    staleTime: 1000 * 60 * 60,
  });

  const perThousand =
    stats && profile && profile.population > 0 ? (stats.points / profile.population) * 1000 : null;
  const carsPerPoint = stats && profile && stats.points > 0 ? profile.population / stats.points : null;

  return (
    <div className="min-h-dvh bg-background">
      <Navbar />
      <BreadcrumbNav
        items={[
          { label: "Hem", path: "/", current: false },
          { label: "Laddstationer", path: "/laddstationer", current: false },
          { label: k, path: null, current: true },
        ]}
      />

      <div className="page-container py-6 text-center md:py-8">
        <h1 className="heading-1 mb-2">Laddstationer {p} {k}</h1>
        <p className="mx-auto max-w-2xl text-sm text-muted-foreground md:text-base">
          Karta över laddstationer och snabbladdare i {k} kommun, {m.county} län. Klicka på en
          station för att se operatör, uttag, effekt och vägbeskrivning.
        </p>
      </div>

      <section className="w-full">
        {mounted ? (
          <Suspense fallback={<MapFallback />}>
            <ChargingMap initialCenter={center} initialZoom={zoomFor(radiusKm)} />
          </Suspense>
        ) : (
          <MapFallback />
        )}
      </section>

      <AppPromoBanner context={k} />

      <main className="section-stripe">
        <div className="page-container space-y-16 pb-16 pt-12">
          <section>
            <h2 className="heading-2 mb-2 text-center">Laddinfrastrukturen {p} {k} i siffror</h2>
            <p className="mx-auto mb-8 max-w-2xl text-center text-muted-foreground">
              Inom {radiusKm} km från centrala {k}, enligt OpenChargeMap.
            </p>
            {isLoading || !stats ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 animate-spin" size={18} /> Hämtar laddstatistik…
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={<MapPin size={22} />} label="Laddstationer" value={formatNumber(stats.stations)} />
                <Stat icon={<PlugZap size={22} />} label="Laddplatser" value={formatNumber(stats.points)} />
                <Stat
                  icon={<Zap size={22} />}
                  label="Stationer med snabbladdning"
                  value={formatNumber(stats.fastStations)}
                />
                <Stat
                  icon={<BatteryCharging size={22} />}
                  label="Högsta effekt"
                  value={stats.maxPowerKw ? `${formatNumber(stats.maxPowerKw)} kW` : "–"}
                />
                {profile && (
                  <>
                    <Stat icon={<Users size={22} />} label="Invånare" value={formatNumber(profile.population)} />
                    <Stat
                      icon={<PlugZap size={22} />}
                      label="Laddplatser per 1 000 invånare"
                      value={perThousand != null ? perThousand.toLocaleString("sv-SE", { maximumFractionDigits: 1 }) : "–"}
                    />
                    <Stat
                      icon={<Building2 size={22} />}
                      label="Invånare per km²"
                      value={profile.density.toLocaleString("sv-SE")}
                    />
                    <Stat icon={<Zap size={22} />} label="Elområde" value={m.area} />
                  </>
                )}
              </div>
            )}
          </section>

          {stats && stats.stations > 0 && (
            <section className="grid gap-6 lg:grid-cols-3">
              <Card className="p-6">
                <h3 className="heading-3 mb-3">Största operatörerna</h3>
                <ul className="space-y-2 text-sm">
                  {stats.operators.map((o) => (
                    <li key={o.name} className="flex justify-between gap-3">
                      <span>{o.name}</span>
                      <span className="text-muted-foreground">{o.stations} st</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="heading-3 mb-3">Vanligaste uttagen</h3>
                <ul className="space-y-2 text-sm">
                  {stats.connectorTypes.map((c) => (
                    <li key={c.type} className="flex justify-between gap-3">
                      <span>{c.type}</span>
                      <span className="text-muted-foreground">{c.count} st</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="heading-3 mb-3">Uttag per effekt</h3>
                <ul className="space-y-2 text-sm">
                  {stats.powerBands.map((b) => (
                    <li key={b.label} className="flex justify-between gap-3">
                      <span>{b.label}</span>
                      <span className="text-muted-foreground">{b.count} st</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          )}

          <section className="mx-auto max-w-3xl space-y-4 text-muted-foreground">
            <h2 className="heading-2 text-center text-foreground">Ladda elbilen {p} {k}</h2>
            <p>
              {k} är en {profile?.sizeLabel ?? "kommun"} i {m.county} län
              {profile ? ` med cirka ${formatNumber(profile.population)} invånare` : ""}.
              {stats
                ? ` Runt centralorten finns ${formatNumber(stats.stations)} registrerade laddstationer med totalt ${formatNumber(stats.points)} laddplatser, varav ${formatNumber(stats.fastPoints)} uttag klarar snabbladdning.`
                : ""}
              {carsPerPoint ? ` Det motsvarar ungefär en laddplats per ${formatNumber(carsPerPoint)} invånare.` : ""}
            </p>
            <p>
              Snabbladdare med DC passar när du är på genomresa eller behöver ladda snabbt, medan
              vanliga AC-laddare vid parkeringar, arbetsplatser och handelsområden passar när bilen
              ändå står still en längre stund.
            </p>
            <p>
              Den billigaste laddningen sker oftast hemma. {k} ligger i elområde {m.area}, och
              vilket elavtal du har avgör vad varje kWh kostar – särskilt om du laddar nattetid när
              spotpriset ofta är lägre.
            </p>
          </section>

          <section className="mx-auto max-w-xl">
            <h2 className="heading-2 mb-2 text-center">Ladda hemma billigare {p} {k}</h2>
            <p className="mb-6 text-center text-muted-foreground">
              Gör en kostnadsfri jämförelse utifrån ditt postnummer, din avtalstyp och förbrukning.
            </p>
            <ElavtalSearchCard title="Jämför elavtal – kostnadsfritt" />
          </section>

          <section className="mx-auto max-w-3xl">
            <h2 className="heading-2 mb-6 text-center">Vanliga frågor om laddning i {k}</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="antal">
                <AccordionTrigger>Hur många laddstationer finns {p} {k}?</AccordionTrigger>
                <AccordionContent>
                  {stats
                    ? `Inom ${radiusKm} km från centrala ${k} finns ${formatNumber(stats.stations)} laddstationer med ${formatNumber(stats.points)} laddplatser registrerade i OpenChargeMap. Utbyggnaden går snabbt, så siffran ändras löpande.`
                    : `Antalet hämtas löpande från OpenChargeMap och visas i kartan ovan.`}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="snabb">
                <AccordionTrigger>Finns det snabbladdare {p} {k}?</AccordionTrigger>
                <AccordionContent>
                  {stats && stats.fastStations > 0
                    ? `Ja, ${formatNumber(stats.fastStations)} stationer i området erbjuder snabbladdning${stats.maxPowerKw ? `, med upp till ${formatNumber(stats.maxPowerKw)} kW` : ""}. Slå på filtret för snabbladdning i kartan för att bara se dem.`
                    : `Använd filtret för snabbladdning i kartan för att se närmaste DC-laddare, även i grannkommunerna.`}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pris">
                <AccordionTrigger>Vad kostar det att ladda {p} {k}?</AccordionTrigger>
                <AccordionContent>
                  Publika laddare prissätts av respektive operatör. Hemma styrs kostnaden av ditt
                  elavtal och spotpriset i elområde {m.area}.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>

          <section>
            <h2 className="heading-3 mb-4 text-center">Laddstationer i närliggande kommuner</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/laddstationer/$slug"
                  params={{ slug: n.slug }}
                  className="rounded-full bg-card px-4 py-2 text-sm shadow-sm hover:text-primary"
                >
                  {n.displayName}
                </Link>
              ))}
              <Link to="/laddstationer" className="rounded-full bg-card px-4 py-2 text-sm font-medium shadow-sm hover:text-primary">
                Alla kommuner
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function MapFallback() {
  return (
    <div className="flex h-[calc(100dvh-220px)] min-h-[480px] w-full items-center justify-center bg-muted text-muted-foreground">
      <Loader2 className="mr-2 animate-spin" size={20} /> Laddar karta…
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="p-5">
      <div className="mb-2 text-primary">{icon}</div>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </Card>
  );
}
